/**
 * Third-party widgets that start when the reader gets near them.
 * ---------------------------------------------------------------------------
 *
 * The Judge.me review widget and carousel, and Klaviyo's embedded signup form,
 * sit near the bottom of their pages, but their scripts started on load: on a
 * throttled phone the CUBE product page fetched 384 KB in 106 requests for a
 * review widget nobody had scrolled to, and every page fetched Klaviyo's form
 * renderer (~110 KB) for the footer form. That bandwidth came out of the
 * page's largest paint.
 *
 *   <cf-defer-load data-kind="judgeme" data-anchor="judgeme_product_reviews">
 *     <template> …widget markup… </template>
 *   </cf-defer-load>
 *
 * The markup stays inert inside the <template> until the element comes within
 * MARGIN of the viewport, then it is put in place. Klaviyo notices a form
 * container added later on its own. Judge.me does not: its loader.js looks for
 * widgets once, when it first runs, so `judgeme` runs the loader again.
 *
 * That re-run is only clean while nothing on the page has started Judge.me
 * before — its core scripts would otherwise load a second time (they still
 * work, they just download twice). So a page with a deferred Judge.me widget
 * should not also carry an eager one: the star badges above such widgets use a
 * theme class instead of `jdgm-widget jdgm-preview-badge`, which keeps them
 * drawn by Judge.me's stylesheet without waking its loader.
 *
 * `data-anchor` names an id inside the template (the review widget's
 * `judgeme_product_reviews`). A link to it, or arriving with it in the URL,
 * loads the content first so the jump has somewhere to land.
 *
 * Crawlers get everything at once: Google's product stars come from JSON-LD
 * the review widget writes after it loads, and a renderer that never scrolls
 * must still see it.
 */
(() => {
  const MARGIN = '600px 0px';
  const CRAWLER = /Googlebot|Google-InspectionTool|Storebot-Google|bingbot|DuckDuckBot|YandexBot|Applebot/i;

  let judgemeQueued = false;

  function rerunJudgemeLoader() {
    if (judgemeQueued) return;
    judgemeQueued = true;

    // One re-run per task, however many Judge.me elements arrived in it.
    setTimeout(() => {
      judgemeQueued = false;
      const jdgm = window.jdgm;

      // Loader not run yet: it will find the widget itself when it does.
      if (!jdgm || !jdgm._loaderExecuted) return;

      const loader = Array.from(document.scripts).find((script) => /\/judgeme-[^/]+\/assets\/loader\.js/.test(script.src));
      if (!loader) return;

      jdgm._loaderExecuted = false;
      const script = document.createElement('script');
      script.src = loader.src;
      document.body.appendChild(script);
    });
  }

  const AFTER_LOAD = {
    judgeme: rerunJudgemeLoader,
  };

  class CfDeferLoad extends HTMLElement {
    connectedCallback() {
      if (this.loaded) return;

      const anchor = this.dataset.anchor;

      if (CRAWLER.test(navigator.userAgent) || !('IntersectionObserver' in window) || (anchor && location.hash === `#${anchor}`)) {
        this.load();
        if (anchor && location.hash === `#${anchor}`) this.jumpToAnchor();
        return;
      }

      this.observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.load();
      }, { rootMargin: MARGIN });
      this.observer.observe(this);

      if (anchor) {
        this.onAnchorClick = (event) => {
          const link = event.target.closest(`a[href="#${anchor}"]`);
          if (!link || this.loaded) return;
          event.preventDefault();
          this.load();
          this.jumpToAnchor();
        };
        document.addEventListener('click', this.onAnchorClick);
      }
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      if (this.onAnchorClick) document.removeEventListener('click', this.onAnchorClick);
    }

    /**
     * Everything of the same kind loads together. Klaviyo fills a late container only if that form is not already
     * on the page: the homepage carries the footer form twice, and the copy that arrived second stayed empty.
     * Together, both fill — and Judge.me's loader runs once for all of its widgets.
     */
    load() {
      if (this.loaded) return;

      const kind = this.dataset.kind;
      const group = kind ? Array.from(document.querySelectorAll(`cf-defer-load[data-kind="${kind}"]`)) : [this];
      if (!group.includes(this)) group.push(this);

      group.forEach((element) => element.reveal());
      AFTER_LOAD[kind]?.();
    }

    reveal() {
      if (this.loaded) return;
      this.loaded = true;
      this.observer?.disconnect();
      if (this.onAnchorClick) document.removeEventListener('click', this.onAnchorClick);

      const template = this.querySelector(':scope > template');
      if (template) template.replaceWith(template.content.cloneNode(true));
      if (this.dataset.kind === 'judgeme') window.cfShrinkJudgemeThumbnails?.(this);
    }

    jumpToAnchor() {
      const target = document.getElementById(this.dataset.anchor);
      if (!target) return;
      history.replaceState(null, '', `#${this.dataset.anchor}`);
      target.scrollIntoView();
    }
  }

  if (!customElements.get('cf-defer-load')) {
    customElements.define('cf-defer-load', CfDeferLoad);
  }
})();

/**
 * Judge.me's review thumbnails at their own size.
 * ---------------------------------------------------------------------------
 *
 * The review cards name the product they are about with a 48px thumbnail, and
 * Judge.me draws it from the product's full photo (cdn.shopify.com/s/files/…,
 * no width): on the FRAME page five of them came to ~1 MB, for 48px each. The
 * thumbnails are lazy, so one whose src is changed the moment Judge.me puts it
 * in the page has not been fetched yet: it asks the Shopify CDN for a 160px
 * copy instead (48px at 3x; the Venice FRAME photo goes from 217 KB to 4.5 KB).
 *
 * Watches every Judge.me widget in the page (the reviews page's all-reviews
 * widget) and the deferred ones above as they are revealed.
 */
(() => {
  const FULL_PHOTO = /^https:\/\/cdn\.shopify\.com\/s\/files\/[^?#]+\.(?:jpe?g|png|webp)(?:\?[^#]*)?$/i;

  function shrink(img) {
    const src = img.getAttribute('src');
    if (!src || !FULL_PHOTO.test(src) || /[?&]width=/.test(src)) return;
    img.setAttribute('src', `${src}${src.includes('?') ? '&' : '?'}width=160`);
  }

  function shrinkWithin(node) {
    if (node.nodeType !== 1) return;
    if (node.matches('.jm-product-variant-card__thumbnail img')) shrink(node);
    node.querySelectorAll('.jm-product-variant-card__thumbnail img').forEach(shrink);
  }

  function watch(root) {
    if (root.cfThumbnailsWatched) return;
    root.cfThumbnailsWatched = true;
    shrinkWithin(root);
    new MutationObserver((records) => {
      records.forEach((record) => {
        if (record.type === 'attributes') shrinkWithin(record.target);
        else record.addedNodes.forEach(shrinkWithin);
      });
    }).observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['src'] });
  }

  window.cfShrinkJudgemeThumbnails = watch;

  function start() {
    document.querySelectorAll('.jdgm-widget').forEach(watch);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
